class ProductSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll('.product-slide');
    this.prevButton = this.querySelector('.prev-slide');
    this.nextButton = this.querySelector('.next-slide');
    this.autoplayButton = this.querySelector('[data-autoplay-button]');
    this.liveRegion = this.querySelector('[data-live-region]');
    this.autoplayText = this.querySelector('[data-autoplay-text]');
    this.currentSlide = 0;
    this.slidesCount = this.slides.length;
    this.autoplay = this.getAttribute('data-autoplay');
    this.autoplayDelay = parseInt(this.getAttribute('data-autoplay-interval')) || 5000;
    this.pauseOnHover = this.getAttribute('data-pause-on-hover');
    this.timer = null;
    
    this.init();
  }

  init() {
    if (!this.slidesCount) return;
    
    // Event listeners for controls
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.navigate('prev'));
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.navigate('next'));
    }

    if (this.autoplayButton) {
      this.autoplayButton.addEventListener('click', () => this.toggleAutoplay());
    }

    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeydown.bind(this));

    // Pause autoplay on hover (optional) and on focus (always)
    if (this.pauseOnHover === 'true' || this.pauseOnHover === '' || this.pauseOnHover === '1') {
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
    }
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.resumeAutoplay());
    
    // Initialize first slide
    this.slides[0].classList.add('active');

    // Start autoplay if enabled
    if (this.autoplay === 'true' || this.autoplay === '' || this.autoplay === '1') {
      this.startAutoplay();
    }
  }

  navigate(direction) {
    if (this.classList.contains('is-animating')) return;

    const currentSlide = this.slides[this.currentSlide];
    let nextIndex = direction === 'next' 
      ? (this.currentSlide + 1) % this.slidesCount
      : (this.currentSlide - 1 + this.slidesCount) % this.slidesCount;
    const nextSlide = this.slides[nextIndex];
    
    this.classList.add('is-animating');

    // Step 1: Show the next slide immediately underneath the current slide
    nextSlide.classList.add('active');
    nextSlide.style.zIndex = '1'; // underneath
    currentSlide.style.zIndex = '2'; // on top
    
    // Step 2: Force reflow to ensure next slide is rendered
    void nextSlide.offsetHeight;
    
    // Step 3: Start slide-out animation on current slide only
    currentSlide.classList.add('slide-out');

    // Update live region for screen readers
    if (this.liveRegion) {
      const slideTitle = nextSlide.querySelector('.slide-title')?.textContent || `Slide ${nextIndex + 1}`;
      this.liveRegion.textContent = `Showing slide ${nextIndex + 1} of ${this.slidesCount}: ${slideTitle}`;
    }

    // Step 4: Clean up after animation
    setTimeout(() => {
      // Remove old slide
      currentSlide.classList.remove('active', 'slide-out');
      currentSlide.style.zIndex = '';
      
      // Finalize new slide
      nextSlide.style.zIndex = '';
      
      this.currentSlide = nextIndex;
      this.classList.remove('is-animating');
    }, 600);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => {
      this.navigate('next');
    }, this.autoplayDelay);
    
    if (this.autoplayText) {
      this.autoplayText.textContent = 'Pause automatic slide show';
    }
    if (this.autoplayButton) {
      this.autoplayButton.setAttribute('aria-label', 'Pause automatic slide show');
    }
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    
    if (this.autoplayText) {
      this.autoplayText.textContent = 'Start automatic slide show';
    }
    if (this.autoplayButton) {
      this.autoplayButton.setAttribute('aria-label', 'Start automatic slide show');
    }
  }

  toggleAutoplay() {
    if (this.timer) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  pauseAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  resumeAutoplay() {
    if (this.autoplay === 'true' || this.autoplay === '' || this.autoplay === '1') {
      this.startAutoplay();
    }
  }

  handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate('prev');
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate('next');
    } else if (event.key === ' ') {
      event.preventDefault();
      this.toggleAutoplay();
    }
  }

  goToSlide(index) {
    if (index >= 0 && index < this.slidesCount && index !== this.currentSlide) {
      const direction = index > this.currentSlide ? 'next' : 'prev';
      this.currentSlide = index - 1; // Will be incremented in navigate
      this.navigate(direction);
    }
  }
}

customElements.define('product-slideshow', ProductSlideshow);
